"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, HTMLMotionProps, motion } from "framer-motion";

import { cn } from "@/lib/utils";

interface WordRotateProps {
  words: string[];
  duration?: number;
  framerProps?: HTMLMotionProps<"h1">;
  className?: string;
  holdToRotate?: boolean;
}

export function WordRotate({
  words,
  duration = 2500,
  framerProps = {
    initial: { opacity: 0, y: -50 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 50 },
    transition: { duration: 0.25, ease: "easeOut" },
  },
  className,
  holdToRotate = false,
}: WordRotateProps) {
  const [index, setIndex] = useState(0);
  const [isHolding, setIsHolding] = useState(false);

  useEffect(() => {
    if (holdToRotate) {
      // Only rotate when holding
      if (isHolding) {
        setIndex(1);
      } else {
        setIndex(0);
      }
    } else {
      // Auto-rotate
      const interval = setInterval(() => {
        setIndex((prevIndex) => (prevIndex + 1) % words.length);
      }, duration);
      return () => clearInterval(interval);
    }
  }, [words, duration, holdToRotate, isHolding]);

  const handlePointerDown = () => {
    if (holdToRotate) {
      setIsHolding(true);
    }
  };

  const handlePointerUp = () => {
    if (holdToRotate) {
      setIsHolding(false);
    }
  };

  return (
    <div 
      className={cn("overflow-hidden py-2", holdToRotate && "cursor-pointer select-none")}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      <AnimatePresence mode="wait">
        <motion.h1
          key={words[index]}
          className={cn(className)}
          {...framerProps}
        >
          {words[index]}
        </motion.h1>
      </AnimatePresence>
    </div>
  );
}
